'use client'
import React from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import * as Accordion from '@radix-ui/react-accordion'

import { cn } from '@/utils/twMerge'
import { PrismicRichText } from '@prismicio/react'
import { PrismicNextImage } from '@prismicio/next'

const TooltipWrapper = dynamic(() => import('../common/TooltipWrapper'), { ssr: false })

interface SecureframePackageProps {
  data: Record<string, any>
}
type ItemObject = Record<string, any>

const DEFAULT_OPEN_VARIATION = 'Compliance Automation'

const SecureframePackageComponent: React.FC<SecureframePackageProps> = ({ data }) => {
  // Check if any item has fundamentals_value
  const hasFundamentals = data?.packagelist.some(
    (item: ItemObject) => item.fundamentals_value && item.fundamentals_value.length > 0,
  )
  // Each slice is its own accordion. Only Compliance Automation matches and starts open.
  const [openValue, setOpenValue] = React.useState(data.variation === DEFAULT_OPEN_VARIATION ? data.variation : '')
  const isOpen = openValue === data.variation
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = React.useState<number>()

  // Radix measures the collapsed box (height 0) and stores 0, so accordion-down never runs.
  // Measure the inner content instead and override --radix-accordion-content-height.
  React.useLayoutEffect(() => {
    const node = contentRef.current
    if (!node) return

    const measure = () => setContentHeight(node.scrollHeight)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [data.packagelist])

  return (
    <section className="mb-6">
      <div className="container">
        <Accordion.Root
          type="single"
          collapsible
          className="bg-transparent"
          value={openValue}
          onValueChange={setOpenValue}>
          <Accordion.Item
            value={data.variation}
            className="group/item rounded-md border border-primary-ink-6 px-4 shadow-dark backdrop-blur-25 transition-colors has-[:focus-visible]:border-primary-green-150 sm:px-6">
            <Accordion.Header>
              <Accordion.Trigger className="group flex w-full items-center justify-between py-5 text-left outline-none hover:opacity-80 focus-visible:outline-none">
                <h4 className="text-xl text-primary-green-150 transition-colors group-has-[:focus-visible]/item:text-primary-green-150">
                  {data.variation}
                </h4>
                <div className="relative h-5 w-5" aria-hidden="true">
                  <Image
                    src="/images/plus.svg"
                    className="absolute transition-opacity duration-200 group-data-[state=closed]:opacity-100 group-data-[state=open]:opacity-0"
                    width={20}
                    height={20}
                    alt=""
                    aria-hidden="true"
                  />
                  <Image
                    src="/images/minus.svg"
                    className="absolute transition-opacity duration-200 group-data-[state=closed]:opacity-0 group-data-[state=open]:opacity-100"
                    width={20}
                    height={20}
                    alt=""
                    aria-hidden="true"
                  />
                </div>
              </Accordion.Trigger>
            </Accordion.Header>

            <Accordion.Content
              forceMount
              inert={!isOpen}
              style={
                contentHeight
                  ? ({ '--radix-accordion-content-height': `${contentHeight}px` } as React.CSSProperties)
                  : undefined
              }
              className="overflow-hidden transition-all duration-300 ease-in-out data-[state=closed]:h-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div ref={contentRef} className="pb-6">
                <div className="grid grid-cols-12">
                  <div className={hasFundamentals ? 'col-span-6' : 'col-span-9'}>
                    <p className="mb-4 font-halyardDisplay text-sm text-primary-ink-60 sm:text-lg">Feature</p>
                  </div>
                  {hasFundamentals && (
                    <div className="col-span-3 text-center">
                      <p className="mb-4 font-halyardDisplay text-sm text-primary-ink-60 sm:text-lg">Fundamentals</p>
                    </div>
                  )}
                  <div className="col-span-3 text-center">
                    <p className="mb-4 font-halyardDisplay text-sm text-primary-ink-60 sm:text-lg">Complete</p>
                  </div>

                  {data?.packagelist.map((item: ItemObject, index: number) => (
                    <React.Fragment key={index}>
                      <div
                        className={cn(
                          'py-4',
                          hasFundamentals ? 'col-span-6' : 'col-span-9',
                          index !== data.packagelist.length - 1 ? 'border-primary-gray-150 border-b' : '',
                        )}>
                        <p className="text-sm text-primary-ink-85 sm:text-base">{item.feature_title}</p>
                      </div>
                      {hasFundamentals && (
                        <div
                          className={cn(
                            'col-span-3 flex items-center justify-center gap-2 py-4 text-sm text-primary-ink-85 sm:text-base',
                            index !== data.packagelist.length - 1 ? 'border-primary-gray-150 border-b' : '',
                          )}>
                          <PrismicRichText field={item.fundamentals_value} />

                          {item.show_tooltip && (
                            <TooltipWrapper text={item.tooltip_text}>
                              <PrismicNextImage field={item.tooltip_icon} width={14} height={14} fallbackAlt="" />
                            </TooltipWrapper>
                          )}
                        </div>
                      )}
                      <div
                        className={cn(
                          'col-span-3 flex items-center justify-center gap-2 py-4 text-sm text-primary-ink-85 sm:text-base',
                          index !== data.packagelist.length - 1 ? 'border-primary-gray-150 border-b' : '',
                        )}>
                        <PrismicRichText field={item.complete_value} />

                        {item.show_tooltip && (
                          <TooltipWrapper text={item.tooltip_text}>
                            <PrismicNextImage field={item.tooltip_icon} width={14} height={14} fallbackAlt="" />
                          </TooltipWrapper>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </div>
    </section>
  )
}

export default SecureframePackageComponent
